import { create } from "zustand";
import {
  emptyPaymentDetailsValidity,
  isPaymentDetailsComplete,
} from "./paymentDetails";
import type { PaymentDetailsValidity } from "./paymentDetails";

export type PaymentGatekeeperOptions = {
  silent?: boolean;
  requireSaved?: boolean;
};

export type PaymentGatekeeperHandler = (
  options?: PaymentGatekeeperOptions
) => Promise<boolean>;

type PaymentGatekeeperStore = {
  handler: PaymentGatekeeperHandler | null;
  secondaryHandler: PaymentGatekeeperHandler | null;
  setHandler: (handler: PaymentGatekeeperHandler | null) => void;
  setSecondaryHandler: (handler: PaymentGatekeeperHandler | null) => void;
  paymentDetailsValidity: PaymentDetailsValidity;
  paymentDetailsComplete: boolean;
  setPaymentDetailsValidity: (validity: PaymentDetailsValidity) => void;
  resetPaymentDetails: () => void;
  run: (options?: PaymentGatekeeperOptions) => Promise<boolean>;
};

export const usePaymentGatekeeperStore = create<PaymentGatekeeperStore>(
  (set, get) => ({
    handler: null,
    secondaryHandler: null,
    setHandler: (handler) => set({ handler }),
    setSecondaryHandler: (secondaryHandler) => set({ secondaryHandler }),
    paymentDetailsValidity: emptyPaymentDetailsValidity,
    paymentDetailsComplete: false,
    setPaymentDetailsValidity: (paymentDetailsValidity) =>
      set({
        paymentDetailsValidity,
        paymentDetailsComplete: isPaymentDetailsComplete(paymentDetailsValidity),
      }),
    resetPaymentDetails: () =>
      set({
        paymentDetailsValidity: emptyPaymentDetailsValidity,
        paymentDetailsComplete: false,
      }),
    run: async (options) => {
      const { handler, secondaryHandler, paymentDetailsComplete } = get();

      // Silent checks are used to decide whether the payment controls should
      // be enabled. They must use the one shared three-field state and never
      // include optional profile or ownership fields.
      if (options?.silent) {
        return paymentDetailsComplete;
      }

      if (handler && !(await handler(options))) {
        return false;
      }

      // Organization details are persisted on the actual payment attempt,
      // but never participate in paymentDetailsComplete or the locked UI.
      if (secondaryHandler && !(await secondaryHandler(options))) {
        return false;
      }

      if (!handler && !secondaryHandler) {
        return true;
      }

      return true;
    },
  })
);
